import { ClinicAuthError } from "@auth/clinicAuthClient";
import { usePrincipalKey } from "@auth/sessionStore";
import {
  type CallSuggestion,
  type CallSuggestionAutofile,
  type CallSuggestionsQuery,
  useCallSuggestions,
} from "@phone/callSuggestionsClient";
import {
  Button,
  CheckIcon,
  Checkbox,
  CrossIcon,
  IconButton,
} from "@scribenote/brian-design-system";
import { useNow } from "@utils/use-now";
import { useEffect, useRef, useState } from "react";

const STATUS = {
  pending: "Suggestions start once the conversation is connected.",
  listening: "Listening for follow-ups…",
  processing: "Updating suggestions…",
  unavailable: "Live suggestions are unavailable for this call.",
  error: "Suggestions could not update. Saved items are still available.",
  ended: "Saved follow-ups from this call.",
};

type Mutation = CallSuggestionsQuery["mutation"];

const Meta = ({ item }: { item: CallSuggestion }) => {
  const parts = [
    ...item.facts.map((text) => ({ text, check: false })),
    ...item.checks.map((text) => ({ text, check: true })),
  ];
  return (
    <>
      {parts.length > 0 && (
        <p className="text-fine-print text-annotation">
          {parts.map((part, index) => (
            <span key={index}>
              {index > 0 && " · "}
              <span className={part.check ? "text-ink-flag" : undefined}>
                {part.text}
              </span>
            </span>
          ))}
        </p>
      )}
      {item.details && (
        <p className="line-clamp-2 whitespace-pre-wrap text-fine-print text-ink-body">
          {item.details}
        </p>
      )}
    </>
  );
};

const SuggestionRow = ({
  item,
  fresh,
  mutation,
  editing,
  onEditing,
}: {
  item: CallSuggestion;
  fresh: boolean;
  mutation: Mutation;
  editing: boolean;
  onEditing: (editing: boolean) => void;
}) => {
  const [ownDraft, setDraft] = useState<{
    title: string;
    details: string | null;
    revision: number;
  } | null>(null);
  // Only one row edits at a time: another row opening its editor closes this one.
  const draft = editing ? ownDraft : null;
  const changed = draft !== null && draft.revision !== item.revision;
  const proposed = item.status === "proposed";
  const approved = item.status === "approved";
  const busy = mutation.isPending;
  const stop = () => {
    setDraft(null);
    onEditing(false);
  };
  const edit = () => {
    mutation.reset();
    onEditing(true);
    setDraft({
      title: item.title,
      details: item.details,
      revision: item.revision,
    });
  };
  return (
    <li className="flex items-start gap-2 py-1.5">
      <span className="pt-0.5">
        <Checkbox
          aria-label={approved ? "In your to-dos" : "Add to my to-dos"}
          title={approved ? "In your to-dos" : "Add to my to-dos"}
          checked={approved}
          // Approval is one-way: an approved row stays checked but inert, without the faded disabled look.
          aria-disabled={approved || undefined}
          disabled={!approved && (!proposed || busy || draft !== null)}
          onChange={() => {
            if (proposed) mutation.mutate({ item, action: "approve" });
          }}
          className={approved ? "cursor-default" : undefined}
        />
      </span>
      <div className="min-w-0 flex-1">
        {draft ? (
          <form
            className="space-y-1"
            onSubmit={(event) => {
              event.preventDefault();
              mutation.mutate(
                {
                  item,
                  action: "edit",
                  body: {
                    revision: draft.revision,
                    title: draft.title.trim(),
                    details: draft.details,
                    client_uuid: item.client_uuid,
                    patient_uuid: item.patient_uuid,
                  },
                },
                { onSuccess: stop },
              );
            }}
          >
            <input
              aria-label="To-do title"
              autoFocus
              maxLength={240}
              value={draft.title}
              onChange={(event) =>
                setDraft({ ...draft, title: event.target.value })
              }
              onKeyDown={(event) => {
                if (event.key === "Escape" && !busy) {
                  event.stopPropagation();
                  stop();
                }
              }}
              className="w-full rounded-paper border border-rule bg-card px-1.5 py-0.5 text-sm font-medium text-ink"
            />
            {changed && (
              <p role="status" className="text-fine-print text-ink-stopped">
                This suggestion changed. Your draft is preserved. Cancel to
                review the latest version.
              </p>
            )}
            <div className="flex gap-1">
              <Button
                size="sm"
                type="submit"
                disabled={!proposed || changed || !draft.title.trim() || busy}
              >
                Save
              </Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={stop}>
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <>
            <div className="flex items-baseline gap-1.5">
              {proposed ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={edit}
                  className="min-w-0 cursor-text truncate rounded-[2px] text-left text-sm font-medium text-ink decoration-rule-strong underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:cursor-default"
                >
                  <span className="sr-only">Edit to-do title: </span>
                  {item.title}
                </button>
              ) : (
                <p className="min-w-0 truncate text-sm font-medium text-ink">
                  {item.title}
                </p>
              )}
              {fresh && (
                <span className="animate-fresh-fade shrink-0 text-fine-print font-semibold text-accent-deep">
                  New
                </span>
              )}
            </div>
            <Meta item={item} />
          </>
        )}
      </div>
      {proposed && !draft && (
        <IconButton
          size="sm"
          aria-label="Dismiss suggestion"
          title="Dismiss"
          disabled={busy}
          onClick={() => mutation.mutate({ item, action: "dismiss" })}
          className="-my-1 -mr-1"
        >
          <CrossIcon />
        </IconButton>
      )}
    </li>
  );
};

/** The filing countdown is running: it has a deadline, is not held and has items to file. */
export const countingDown = (
  autofile: CallSuggestionAutofile | undefined,
  proposed: number,
): autofile is CallSuggestionAutofile & { deadline: string } =>
  Boolean(autofile?.deadline) && !autofile?.held && proposed > 0;

/** Fraction of the countdown left, and whole seconds left. */
export const useCountdown = (
  autofile: CallSuggestionAutofile,
  deadline: string,
  receivedAt: number,
) => {
  const tick = useNow(250);
  // On brian's clock (`autofile.now` as of `receivedAt`), so a skewed browser clock cannot shorten or stretch it.
  const serverNow = Date.parse(autofile.now) + Math.max(0, tick - receivedAt);
  const total = Math.max(1, autofile.seconds) * 1000;
  const remaining = Math.min(
    total,
    Math.max(0, Date.parse(deadline) - serverNow),
  );
  return { fraction: remaining / total, left: Math.ceil(remaining / 1000) };
};

const Countdown = ({
  autofile,
  deadline,
  receivedAt,
  count,
  pending,
  framed,
  onHold,
}: {
  autofile: CallSuggestionAutofile;
  deadline: string;
  receivedAt: number;
  count: number;
  pending: boolean;
  framed: boolean;
  onHold: () => void;
}) => {
  const { fraction, left } = useCountdown(autofile, deadline, receivedAt);
  return (
    <div className="space-y-1.5 border-t border-rule pt-2">
      <div className="flex min-h-7 items-center justify-between gap-2">
        {/* role="timer" is not live, so the per-tick text is not announced. */}
        <p role="timer" className="text-xs text-ink-body">
          {left > 0 ? `Adding ${count} to to-dos in ${left}s` : "Adding…"}
        </p>
        {left > 0 && (
          <Button
            size="sm"
            variant="outline"
            disabled={pending}
            onClick={onHold}
          >
            Hold
          </Button>
        )}
      </div>
      {!framed && (
        <div aria-hidden className="h-0.5 overflow-hidden rounded-full bg-rule">
          <div
            className="h-full bg-accent transition-[width] duration-300 ease-linear motion-reduce:transition-none"
            style={{ width: `${fraction * 100}%` }}
          />
        </div>
      )}
    </div>
  );
};

// The server owns filing: it sets `deadline` once the call has ended and gone idle, and files the
// remaining proposed items itself. This bar only reflects that clock and holds or resumes it.
const AutofileBar = ({
  autofile,
  receivedAt,
  items,
  proposed,
  pending,
  framed,
  onHeld,
}: {
  autofile: CallSuggestionAutofile;
  receivedAt: number;
  items: number;
  proposed: number;
  pending: boolean;
  framed: boolean;
  onHeld: (held: boolean) => void;
}) => {
  if (countingDown(autofile, proposed))
    return (
      <Countdown
        key={autofile.deadline}
        autofile={autofile}
        deadline={autofile.deadline}
        receivedAt={receivedAt}
        count={proposed}
        pending={pending}
        framed={framed}
        onHold={() => onHeld(true)}
      />
    );
  if (autofile.held && proposed > 0)
    return (
      <div className="flex min-h-7 items-center justify-between gap-2 border-t border-rule pt-2">
        <p role="status" className="text-xs text-ink-body">
          Countdown paused
        </p>
        <Button
          size="sm"
          variant="outline"
          disabled={pending}
          onClick={() => onHeld(false)}
        >
          Resume
        </Button>
      </div>
    );
  // Filing deletes the call's suggestion rows, so a filed call lists none.
  if (autofile.filed_at && items === 0)
    return (
      <p
        role="status"
        className="flex items-center gap-1 border-t border-rule pt-2 text-xs text-ink-body"
      >
        <CheckIcon aria-hidden className="text-ink-recorded" />
        Added to to-dos
      </p>
    );
  return null;
};

const Suggestions = ({
  query,
  title,
  framed,
}: {
  query: CallSuggestionsQuery;
  title: string;
  framed: boolean;
}) => {
  const [editingUuid, setEditingUuid] = useState<string | null>(null);
  // True only while a countdown hold is this panel's own, taken when a title edit opened.
  const heldByEdit = useRef(false);
  // Uuids present at first load; anything after is marked "New" until its badge fades.
  const [initial, setInitial] = useState<ReadonlySet<string> | null>(null);
  if (initial === null && query.data)
    setInitial(new Set(query.data.items.map((item) => item.uuid)));
  const items = query.data?.items ?? [];
  const autofile = query.data?.autofile;
  const running = Boolean(autofile?.deadline) && !autofile?.held;
  const { mutate: setHeld } = query.autofile;

  useEffect(() => {
    const held = heldByEdit;
    return () => {
      if (held.current) {
        held.current = false;
        setHeld(false);
      }
    };
  }, [setHeld]);

  const setRowEditing = (uuid: string, active: boolean) => {
    if (active) {
      setEditingUuid(uuid);
      if (running && !heldByEdit.current) {
        heldByEdit.current = true;
        setHeld(true);
      }
    } else if (editingUuid === uuid) {
      setEditingUuid(null);
      if (heldByEdit.current) {
        heldByEdit.current = false;
        setHeld(false);
      }
    }
  };

  // Dismissed and withdrawn rows are not shown, except one whose editor is still open.
  const visible = items.filter(
    (item) =>
      item.uuid === editingUuid ||
      item.status === "proposed" ||
      item.status === "approved",
  );
  const proposed = items.filter((item) => item.status === "proposed").length;
  const conflict =
    query.mutation.error instanceof ClinicAuthError &&
    query.mutation.error.status === 409;
  return (
    <section
      data-dd-privacy="hidden"
      className="mt-3 space-y-1 border-t border-rule pt-3"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-2">
        <h3 className="text-sm font-semibold text-ink">{title}</h3>
        <p className="text-fine-print text-annotation" role="status">
          {query.isPending
            ? "Loading follow-ups…"
            : query.isError
              ? "Couldn't load suggestions."
              : STATUS[query.data!.status]}
        </p>
      </div>
      {query.isError && (
        <Button size="sm" variant="ghost" onClick={() => void query.refetch()}>
          Retry
        </Button>
      )}
      {query.mutation.isError && (
        <p role="alert" className="text-xs text-ink-stopped">
          {conflict
            ? "This item changed. Review the latest version before trying again."
            : "Couldn't save that change. Please try again."}
        </p>
      )}
      {query.autofile.isError && (
        <p role="alert" className="text-xs text-ink-stopped">
          Couldn't update the countdown. Please try again.
        </p>
      )}
      {visible.length > 0 && (
        <ul className="divide-y divide-rule">
          {visible.map((item) => (
            <SuggestionRow
              key={item.uuid}
              item={item}
              fresh={initial !== null && !initial.has(item.uuid)}
              mutation={query.mutation}
              editing={item.uuid === editingUuid}
              onEditing={(active) => setRowEditing(item.uuid, active)}
            />
          ))}
        </ul>
      )}
      {autofile && (
        <AutofileBar
          autofile={autofile}
          receivedAt={query.dataUpdatedAt}
          items={items.length}
          proposed={proposed}
          pending={query.autofile.isPending}
          framed={framed}
          onHeld={setHeld}
        />
      )}
    </section>
  );
};

/** For a parent that already reads the call's suggestions; a second reader would double every refresh. */
export const CallSuggestions = ({
  callUuid,
  query,
  title = "Call follow-ups",
  framed = false,
}: {
  callUuid: string;
  query: CallSuggestionsQuery;
  title?: string;
  /** The enclosing bubble draws the countdown on its border instead of a bar. */
  framed?: boolean;
}) => {
  const principal = usePrincipalKey();
  return (
    <Suggestions
      key={`${principal}:${callUuid}`}
      query={query}
      title={title}
      framed={framed}
    />
  );
};

export const CallSuggestionsPanel = ({ callUuid }: { callUuid: string }) => {
  const query = useCallSuggestions(callUuid);
  return <CallSuggestions callUuid={callUuid} query={query} />;
};
